import * as React from 'react'

import { Autocomplete, AutocompleteProps } from '@mui/material'
import RdnaTextInput, { RdnaTextInputProps } from '../RdnaTextInput'
import { uniqueId } from 'lodash-es'
import { DropdownArrowIcon } from '../RdnaSelect'

type AutoCompleteBoolean = boolean | undefined
type RdnaAutocompleteProps<
  Multiple extends AutoCompleteBoolean,
  DisableClearable extends AutoCompleteBoolean,
  FreeSolo extends AutoCompleteBoolean
> = { useValueOption?: boolean; textInputProps: RdnaTextInputProps } & Omit<
  AutocompleteProps<{ label: React.ReactNode; value: any; groupId?: string }, Multiple, DisableClearable, FreeSolo>,
  'renderInput'
>

export default function RdnaAutocomplete<
  Multiple extends AutoCompleteBoolean,
  DisableClearable extends AutoCompleteBoolean,
  FreeSolo extends AutoCompleteBoolean
>({
  useValueOption = false,
  textInputProps,
  // autocomplete props
  options,
  groupBy,
  onInputChange,
  value,
  ...restOfAutocompleteProps
}: RdnaAutocompleteProps<Multiple, DisableClearable, FreeSolo>) {
  const _id = 'autocomplete_' + textInputProps?.name + '_' + uniqueId('')

  return (
    <Autocomplete
      disablePortal
      id={_id}
      data-testid={`${textInputProps.name}-autocomplete`}
      options={options}
      disabled={!!textInputProps?.disabled}
      groupBy={!groupBy ? option => option?.groupId || '' : groupBy}
      sx={{ input: { paddingRight: '24px !important' } }}
      renderInput={params => <RdnaTextInput {...params} {...textInputProps} />}
      value={value === '' ? undefined : value}
      disableClearable={!value as any}
      popupIcon={<DropdownArrowIcon />}
      onInputChange={
        !!onInputChange
          ? (event, newInputValue, reason) => {
              let _newInputValue = newInputValue

              if (useValueOption && reason === 'reset') {
                // @ts-ignore - bc of useValueOption, we know newInputValue is not an object
                _newInputValue =
                  options?.find(option => {
                    return option.value === newInputValue
                  })?.label || ''
              }

              onInputChange(event, _newInputValue, reason)
            }
          : undefined
      }
      {...restOfAutocompleteProps}
    />
  )
}

export const makeAutoCompleteProps = (muiProps: any, useValueOption = false) => {
  return {
    value: !!muiProps.value ? muiProps.value : null,
    /*
     The AutoComplete component uses the entire option as its value. (behaves different from Select component)
     So when options are [{label: 'a', value: '123'}, ...], and label 'a' gets selected, then the newValue is {label: 'a', value: '123'}.
     This shouldn't be a problem if you expect an object as a value, but some cases require just the value (or '123' in this example).
     There is no way to tell the component to render just the value, so we have to change it onChange.
     Set useValueOption to 'true' to use the value field in the option object.
     This will also adjust onInputChange to make sure it renders the correct label.
     */
    // @ts-ignore
    onChange: (_, newValue) => {
      muiProps.onChange(useValueOption ? newValue?.value : newValue)
    },
    useValueOption
  }
}

export const makeTextInputProps = (muiProps: any) => {
  return {
    name: muiProps.name,
    error: muiProps.error,
    helperText: muiProps.helperText
  }
}
